import React, { useEffect, useState, useRef } from "react";
import { fetchJson, post } from "../../lib/Fetching";
import Link from "../Link";
import ContainerAliquotStatus from "./ContainerAliquotStatus";
import SampleSummaryView from "../sample_reporting/SampleSummaryView";
import GanntChartContainerView from "../tracking/GanntChartContainerView";
import SampleResults from "../sample_reporting/SampleResults";
import SingleSample from "../sample_reporting/SingleSample";
import DilutionsInput from "./DilutionsInput";
import Tooltip from "../common/Tooltip";
import UnsavedResultsNotification from "./UnsavedResultsNotification";
import { validResult, clearResult } from "./DilutionUtils";
import Checkbox from "../common/form/Checkbox";
import classNames from "classnames";
import {
  playErrorSound,
  playSuccess,
  handleError,
  showErrorAlert,
  playRetainThisPlate,
} from "../../lib/StatusSounds";
import { takeAliquotPhoto } from "../../lib/TakePhoto";
import Button from "../Button";
import Swal from "sweetalert2";

const useFocus = () => {
  const htmlElRef = useRef(null);
  const setFocus = () => {
    htmlElRef.current && htmlElRef.current.focus();
  };

  return [htmlElRef, setFocus];
};

export default function PlateCounting({ laboratory_company_id }) {
  const [containerStatus, setContainerStatus] = useState({ generation: 0, containerId: "" });
  const [allResultsGeneration, setAllResultsGeneration] = useState(0);
  const [ganntChartGeneration, setGanntChartGeneration] = useState(0);
  const [aliquot, setAliquot] = useState(null);
  const [excludeDilutions, setExcludeDilutions] = useState(true);
  const [showFullSampleInfo, setShowFullSampleInfo] = useState(false);
  const [showRemovedItems, setShowRemovedItems] = useState(false);
  const [showLabResults, setShowLabResults] = useState(false);
  const [error, setError] = useState(null);

  const [mode, setMode] = useState("normal"); // can be normal or non-detect

  const [inputText, setInputText] = useState("");
  const [inputFocusRef, setInputFocus] = useFocus();

  const [dilutionTests, setDilutionTests] = useState(null);

  const excludeDilutionsRegex = /\(-\d+\)/;

  const handleResultsChanged = () => {
    setInputFocus();
    setAllResultsGeneration(allResultsGeneration + 1);
  };

  useEffect(() => {
    setInputFocus();
  }, []);

  const aliquotReference = () => {
    if (!inputText) return null;

    const regex = /A\w+/i;
    const match = regex.exec(inputText);

    if (!match) return null;
    return match[0];
  };

  const saveAsNonDetect = (aliquotId) => {
    if (!aliquotId) {
      showErrorAlert("Cannot Save");
      return;
    }

    post("/sample_testing/plate_counting/save_single_aliquot_as_non_detect", {
      aliquot_id: aliquotId,
    })
      .then((json) => {
        if (json.error) {
          handleError(json.error);
        } else {
          setAliquot(null);
          playSuccess();
          fetchAliquot();
        }
      })
      .catch(handleError);
  };

  const showRetainedAliquotAlert = (aliquot) => {
    if (aliquot.retained) {
      playRetainThisPlate();
      const swalBoostrap = Swal.mixin({
        customClass: {
          confirmButton: "btn btn-success"
        },
        buttonsStyling: false
      });
      swalBoostrap.fire({
        title: "Retain this aliquot",
        text: "This aliquot is marked for retention. Please retain the plate!",
        icon: "warning",
        confirmButtonText: "OK",
        didOpen: () => {
          const popup = swalBoostrap.getPopup();
          popup.addEventListener("keydown", function (e) {
            if (e.key === "Enter") {
              e.preventDefault();     // Stop default "confirm" behavior
              e.stopPropagation();    // Stop event from bubbling up
            }
          });
        }
      });
    }
  };

  const fetchAliquot = () => {
    if (!aliquotReference()) return null;
    return fetchJson("/scheduling/aliquots.json", {
      search: aliquotReference(),
    }).then((json) => {
      if (json.error) {
        setError(json.error);
        setAliquot(null);
        playErrorSound();
        return Promise.reject();
      }
      setInputText("");
      setError(null);
      setAliquot(json);
      showRetainedAliquotAlert(json);
      return json;
    });
  };

  const showContainerAliquots = () => {
    setAliquot(null);
    if (inputText) {
      setContainerStatus({
        generation: containerStatus.generation + 1,
        containerId: inputText,
      });
      setInputText("");
    }
  };

  const updateAliquot = (aliquot) => {
    setError(null);
    setInputText("");
    setAliquot(aliquot);
    showRetainedAliquotAlert(aliquot);
  };

  const handleDilutionsLoaded = (dilutionTests) => {
    setDilutionTests(dilutionTests);
    setContainerStatus({
      generation: containerStatus.generation + 1,
      containerId: containerStatus.containerId,
    });
  };

  const handleInputPressEnter = (e) => {
    if (inputText && e.key === "Enter") {
      if (dilutionTests && dilutionTests.filter((dt) => validResult(dt)).length > 0) {
        showErrorAlert(
          "WARNING: There are un-saved results on this page. You cannot search for another aliquot or container until they are saved or cleared."
        );
        return;
      }

      if (aliquotReference()) {
        if (isNonDetectMode) {
          setAliquot(null);
          fetchAliquot().then((aliquot) => saveAsNonDetect(aliquot.id));
        } else {
          fetchAliquot();
        }
        return;
      }
      if (inputText[0].toUpperCase().toUpperCase() === "I") {
        showContainerAliquots();
        return;
      }
      showErrorAlert("You can only scan aliquots or containers/items here.");
    }
  };

  const clickedNonDetectMode = (e) => {
    setMode(e.target.checked ? "non-detect" : "normal");
    setInputFocus();
  };

  const isNonDetectMode = mode === "non-detect";

  return (
    <div
      className={classNames(
        { "non-detect-mode": mode === "non-detect" },
        "plate-counting-container"
      )}
    >
      {isNonDetectMode && (
        <div className="non-detect-alert">
          WARNING: You are in Non Detect MODE!
          <br />
          Results linked to all scanned aliquots will be added as Non-DETECT when they are
          scanned!!
        </div>
      )}
      <div className="head">
        {error && <div className="alert alert-danger">{error}</div>}
        <input
          value={inputText}
          onChange={(e) => {
            setInputText(e.target.value);
          }}
          onKeyPress={handleInputPressEnter}
          placeholder="Scan Container / Aliquot"
          className="input-lg scan-input"
          ref={inputFocusRef}
          style={{ marginRight: 10 }}
        />
        <Tooltip id={"scan-container-aliquot"} place={"bottom"}>
          <p>
            Here you can scan a container or an aliquot. The container can be any item that has
            had other items scanned into it, e.g. a Tray, Incubator, etc
          </p>
        </Tooltip>
        &nbsp;&nbsp; &nbsp;&nbsp;
        <label>
          <Checkbox checked={mode === "non-detect"} onChange={clickedNonDetectMode} />{" "}
          NON-DETECT MODE
        </label>
        &nbsp;&nbsp;
        <Tooltip id={"non-detect-mode-tip"} place={"bottom"}>
          <p>
            When NON-DETECT mode is active, all the results for the scanned aliquots will
            automatically be added as Non Detect when they are scanned.
          </p>
        </Tooltip>
        &nbsp;&nbsp;
        <UnsavedResultsNotification dilutionTests={dilutionTests} />
      </div>

      {/* Retain banner */}
      {aliquot && aliquot.retained && (
        <div className="alert alert-danger" style={{ marginBottom: 20, textAlign: 'center', fontSize: '16px', fontWeight: 'bold' }}>
          <i className="fa fa-exclamation-triangle" style={{ marginRight: 8 }}></i>
          Retain this aliquot
        </div>
      )}

      {aliquot && (
        <div>
          {showFullSampleInfo && (
            <div style={{ marginBottom: 20 }}>
              <SingleSample sample_id={aliquot.sample_id} clientView={false} />
            </div>
          )}

          <div className="section-header" style={{ marginBottom: -16 }}>
            <div className="heading sample">Sample #{aliquot.sample_id}</div>
            <div className="options" style={{ paddingTop: 4 }}>
              <Link onClick={() => setShowFullSampleInfo(!showFullSampleInfo)}>
                Show Full Sample Information
              </Link>
            </div>
          </div>

          <div className="sample-summary-view-container">
            <SampleSummaryView id={aliquot.sample_id} simpleView={true} />
          </div>
        </div>
      )}

      {aliquot &&
        showLabResults && (
          <div className="basic-panel" style={{ flexGrow: 1, marginBottom: 20 }}>
            <div className="section-header">
              <div className="heading">
                All Sample Results &nbsp;&nbsp;
                <Link onClick={() => setShowLabResults(false)}>Close</Link>
              </div>
              <div className="options" />
            </div>
            <div className="sample-summary">
              <SampleResults
                key={aliquot.sample_id + "_" + allResultsGeneration}
                sample={{ identifier: aliquot.sample_id }}
                onClose={() => setShowLabResults(false)}
                clientView={false}
                searchType={"laboratory_results"}
              />
            </div>
          </div>
        )}

      {aliquot && (
        <div className="aliquot-results-container">
          <div className="basic-panel" style={{ flexGrow: 1 }}>
            <div className="section-header">
              <div className="heading">
                Related Dilutions for {aliquot.identifier} -
                {aliquot.label_information && aliquot.label_information.join(", ")}
                &nbsp;&nbsp;
                <Tooltip id={"related-dilutions"}>
                  <p>
                    Here are all dilutions/sample-tests related to the tests on the current
                    aliquot.
                  </p>
                  <p>The row(s) highlighted in blue belong to the current scanned aliquot.</p>
                  <p>
                    This is determined by looking at the technique(s) associated to the current
                    aliquot and finding other tests on the sample with the same technique(s).
                  </p>
                  <p>
                    More detail: It finds the parent aliquot (e.g. the blend aliquot), then
                    finds all the tests associated to any children/sub aliquots of that which
                    have matching technique(s).
                  </p>
                </Tooltip>
              </div>
              <div className="options">
                <Button
                  title={false}
                  icon="fa-camera"
                  onClick={() =>
                    takeAliquotPhoto(
                      aliquot.id,
                      aliquot.identifier +
                        (aliquot.label_information
                          ? " - " + aliquot.label_information.join(", ")
                          : "")
                    )
                  }
                  tiny
                />
              </div>
            </div>
            <div className="sample-summary">
              <DilutionsInput
                highlight={isNonDetectMode ? false : true}
                aliquot_id={aliquot.id}
                onLoaded={handleDilutionsLoaded}
                onResultsChange={handleResultsChanged}
              />
            </div>
          </div>
          <div className="basic-panel all-sample-results">
            <div className="section-header">
              <div className="heading">
                All Sample Results &nbsp;&nbsp;
                <Tooltip id={"all-sample-results"}>
                  <p>
                    Here are <b>all</b> the available results linked to the current sample. (It
                    will not show unscheduled tests)
                  </p>
                  <p>
                    These results will automatically refresh whenever new results are added via
                    the "Related Dilutions" section.
                  </p>
                  <p>
                    Warning: These results may refresh before the system has had chance to
                    generate any calculated results, so you may need to click "Refresh" after a
                    few moments if you want to see them.
                  </p>
                </Tooltip>
              </div>
              <div className="options">
                <Link onClick={() => setAllResultsGeneration(allResultsGeneration + 1)}>
                  <i className="fa fa-refresh" />
                  &nbsp; Refresh
                </Link>
                &nbsp;&nbsp;|&nbsp;&nbsp;
                <Link onClick={() => setShowLabResults(!showLabResults)}>
                  Show Laboratory Results
                </Link>
                &nbsp;&nbsp;&nbsp;
                <label>
                  <input
                    type="checkbox"
                    checked={excludeDilutions}
                    onChange={(e) => setExcludeDilutions(!excludeDilutions)}
                  />{" "}
                  Exclude Dilutions?
                </label>
              </div>
            </div>
            <div className="sample-summary">
              <SampleResults
                key={aliquot.sample_id + "_" + allResultsGeneration}
                sample={{ identifier: aliquot.sample_id }}
                onClose={() => {}}
                clientView={false}
                searchType={"all_results"}
                excludeTestNameRegex={excludeDilutions ? excludeDilutionsRegex : null}
              />
            </div>
          </div>
        </div>
      )}

      {containerStatus.containerId && (
        <div>
          <div className="basic-panel">
            <div className="section-header">
              <div className="heading">
                Aliquots within Container {containerStatus.containerId}
                &nbsp;&nbsp;
                <Tooltip id={"aliquots-within-container"} place="bottom">
                  <p>
                    Here are all the aliquots that have been scanned into this container via
                    the item-tracking or tray system.
                  </p>
                  <p>
                    Aliquots will show in green when all the results linked to the aliquot have
                    been entered.
                  </p>
                  <p>
                    This list is automatically refreshed whenever a new result is added via the
                    "Related Dilutions" section or a new aliquot is scanned.
                  </p>
                  <p>
                    The numbers indicate how many results have been entered and how many
                    results in total are linked to that aliquot. For example, 3 / 5 indicates
                    that 3 out of 5 results have been entered.
                  </p>
                </Tooltip>
              </div>
              <div className="options" />
            </div>
            <div style={{ padding: 10, paddingBottom: 0 }}>
              <ContainerAliquotStatus
                key={containerStatus.generation}
                containerId={containerStatus.containerId}
                highlightAliquotId={aliquot && aliquot.id}
                handleAliquotClick={(aliquot) => updateAliquot(aliquot)}
              />
            </div>
          </div>
          <div className="basic-panel" style={{ marginTop: 20 }}>
            <div className="section-header">
              <div className="heading">
                Gannt Chart for all items in container {containerStatus.containerId}
                &nbsp;&nbsp;
                <Tooltip id={"gannt-for-container"} place="bottom">
                  <p>This is the Gannt Chart for all the items in the current container.</p>
                  <p>It only shows items that have not yet been removed.</p>
                  <p>
                    This is only loaded when the container is first scanned. Click 'refresh' to
                    update it.
                  </p>
                  <p>
                    This could be used to see if the items in the container are ready to be
                    scanned out.
                  </p>
                </Tooltip>
                &nbsp;&nbsp;
                <Link onClick={() => setGanntChartGeneration(ganntChartGeneration + 1)}>
                  <i className="fa fa-refresh" />
                  &nbsp; Refresh
                </Link>
              </div>
              <div className="options">
                <label>
                  <input
                    type="checkbox"
                    checked={showRemovedItems}
                    onChange={(e) => {
                      setShowRemovedItems(!showRemovedItems);
                      setGanntChartGeneration(ganntChartGeneration + 1);
                    }}
                  />{" "}
                  Show full history?
                </label>
              </div>
            </div>
            <div style={{ padding: 10, paddingBottom: 0 }}>
              <GanntChartContainerView
                key={ganntChartGeneration}
                id={containerStatus.containerId}
                showRemovedItems={showRemovedItems}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
